import DutyStatusLog from "../../../../model/DutyStatusLog";
import dayjs from "../../../../utils/dayjs";
import { dstCalculator } from "../../../../utils/date";
import constants from "../../../../constants";
import { getCurrentVehicle } from "../../../../utils/device";
import { getServerDate } from "../../../../utils/storage/date";
import idSerializer from "geotab-uuid";

/* eslint-disable @typescript-eslint/naming-convention */
export class MalfunctionDutyStatusLog extends DutyStatusLog {
    _clearedTime?: string;
    _status?: string;
    _date?: string;
    _header?: string;
}

const DATE_FORMAT = "lll";
const MALFUNCTION_KEY = "#malfunction";
const DIAGNOSTIC_KEY = "#diagnostic";
const CLEARED_REGEXP = ["UserClear", "SystemClear", "SystemClearDriving", "SystemDiagnosticClear",
    "SystemDiagnosticClearDriving", "UserDiagnosticClear", "UserMalfunctionClear"];
const diagnosticRegExp = /Diagnostic/gi;
const MANUAL_PROP = "Manual";

export const getMalfunctionDutyStatusLog = (log: DutyStatusLog, statusHeader: string, clearedTime?: string) => {
    let malfunctionLog = new MalfunctionDutyStatusLog(log);
    malfunctionLog._clearedTime = clearedTime ? dayjs(dstCalculator.toLocalDateTime(clearedTime)).format(DATE_FORMAT) : clearedTime;
    malfunctionLog._date = dayjs(dstCalculator.toLocalDateTime(log.dateTime)).format(DATE_FORMAT);
    malfunctionLog._header = statusHeader + " " + log.malfunction;
    return malfunctionLog;
};

export const populateAndGroupLogs = (collection: DutyStatusLog[], filterStartDateMs: number) => {
    // Filter UnidentifiedDrivingDiagnostics so that the diagnostics indicator does not show up for logs
    // that are older than 14 days in Canada and 7 days in US
    let groupedCollection = collection.reduce((acc, log) => {
        let groupKey = `${ log.status }${ (log.malfunction?.match(diagnosticRegExp) ? DIAGNOSTIC_KEY : MALFUNCTION_KEY) }`;
        if (log.status === "UnidentifiedDrivingCompliance" && new Date(log.dateTime).getTime() < filterStartDateMs) {
            return acc;
        }
        if (acc[groupKey]) {
            acc[groupKey].push(log);
        } else {
            acc[groupKey] = [log];
        }

        return acc;
    }, {});
    let resultCollection: Map<string, MalfunctionDutyStatusLog[]> = new Map();
    let malfunctionState = false;
    let diagnosticState = false;

    Object.keys(groupedCollection).forEach(groupKey => {
        let group: DutyStatusLog[]  = groupedCollection[groupKey];

        group.sort((log1, log2) => log2.getDate().getTime() - log1.getDate().getTime());

        let malfunctionLogsByType: MalfunctionDutyStatusLog[] = [];

        let clearedTime: string | undefined = undefined;
        let groupStatusHeader = group[0].status.replace("Compliance", "").split(constants.CAMELCASE_SEPARATOR_REGEXP).join(constants.SPACE_SEPARATOR);
        group.forEach((log) => {
            if (log.malfunction && CLEARED_REGEXP.indexOf(log.malfunction) > -1) {
                clearedTime = log.dateTime;
            } else {
                let malfunctionLog = getMalfunctionDutyStatusLog(log, groupStatusHeader, clearedTime);
                malfunctionLogsByType.push(malfunctionLog);
                if (log.malfunction === "Malfunction" && !malfunctionLog._clearedTime) {
                    malfunctionState = true;
                }
                if (log.malfunction === "Diagnostic" && !malfunctionLog._clearedTime) {
                    diagnosticState = true;
                }
            }
        });

        if (malfunctionLogsByType.length) {
            if (!malfunctionLogsByType[0]._clearedTime) {
                // insert active malfunctions/diagnostics at the start of the collection
                resultCollection = new Map([
                    [groupKey, malfunctionLogsByType],
                    ...Array.from(resultCollection.entries())
                ]);
            } else {
                // insert cleared malfunctions/diagnostics at the end of the collection
                resultCollection = new Map([
                    ...Array.from(resultCollection.entries()),
                    [groupKey, malfunctionLogsByType]
                ]);
            }
        }
    });

    return { resultCollection, malfunctionState, diagnosticState };
};

export const filterMalfunctions = (log: DutyStatusLog) => {
    let currentVehicle = getCurrentVehicle();
    return currentVehicle && log.isMalfunctionLog() && log.getDeviceId() === currentVehicle.id;
};

export const getClearingLog = (log: MalfunctionDutyStatusLog) => {
    let newLog = {} as any;
    newLog.id = idSerializer.get();
    newLog.malfunction = `User${ log.malfunction }Clear`;
    newLog.status = log.status;
    newLog.dateTime = getServerDate().toISOString();
    newLog.origin = MANUAL_PROP;
    newLog.device = log.device;
    newLog.driver = { id: "NoUserId" };
    return newLog;
};